import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-16 px-4 sm:px-6 lg:px-8 font-sans transition-colors duration-300">
      <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 transition-colors duration-300">
        {/* Header Section */}
        <header className="bg-slate-900 py-12 px-8 text-center text-white">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight uppercase border-b-4 border-white inline-block pb-2 mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg font-semibold text-slate-300">
          Brightex Solutions Pvt Ltd
          </p>
          <p className="text-sm text-slate-400 mt-2 tracking-widest uppercase">
            Effective Date: February 2026
          </p>
        </header>

        {/* Content Body */}
        <div className="p-8 md:p-14 space-y-12 text-gray-700 dark:text-gray-300 leading-relaxed transition-colors duration-300">
          {/* Section 1 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 border-l-4 border-slate-900 dark:border-white pl-4 transition-colors duration-300">
              1. INTRODUCTION
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed transition-colors duration-300">
              Welcome to lapzop.com. We value your privacy and are committed to
              protecting your personal data. This Privacy Policy explains how
              Brightex Solutions Private Limited collects, uses, and safeguards
              your information when you visit our website or make a purchase.
            </p>
          </section>

          {/* Section 2 - Information Collection */}
          <section className="bg-slate-50 dark:bg-gray-700/50 p-6 rounded-xl border border-slate-200 dark:border-gray-600 transition-colors duration-300">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 border-l-4 border-slate-900 dark:border-white pl-4 transition-colors duration-300">
              2. INFORMATION WE COLLECT
            </h3>
            <p className="mb-4 text-sm text-slate-500 dark:text-gray-400 italic transition-colors duration-300">
              We collect information necessary to provide you with a seamless
              shopping experience:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: "Personal Identity Data",
                  desc: "Name, email address, phone number, and shipping/billing address.",
                  icon: "ID",
                },
                {
                  title: "Transaction Data",
                  desc: "Details about payments and products you have purchased from us.",
                  icon: "$",
                },
                {
                  title: "Technical Data",
                  desc: "IP address, browser type, and device information used to access the site.",
                  icon: "💻",
                },
                {
                  title: "Usage Data",
                  desc: "Information about how you use our website, products, and services.",
                  icon: "📈",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-slate-100 dark:border-gray-600 shadow-sm transition-colors duration-300"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-2 py-1 rounded font-bold transition-colors duration-300">
                      {item.icon}
                    </span>
                    <strong className="text-slate-900 dark:text-white text-sm transition-colors duration-300">
                      {item.title}
                    </strong>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 border-l-4 border-slate-900 dark:border-white pl-4 transition-colors duration-300">
              3. HOW WE USE YOUR INFORMATION
            </h3>
            <div className="space-y-4">
              {[
                {
                  step: "01",
                  title: "Order Fulfillment",
                  content:
                    "To process transactions and deliver products via our shipping partner, Shiprocket.",
                },
                {
                  step: "02",
                  title: "Communication",
                  content:
                    "To send order updates and respond to customer support inquiries.",
                },
                {
                  step: "03",
                  title: "Improvement",
                  content:
                    "To analyze website traffic and improve our product offerings.",
                },
                {
                  step: "04",
                  title: "Compliance",
                  content:
                    "To meet legal and tax obligations, including GST reporting.",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <span className="text-slate-300 dark:text-slate-600 font-black text-2xl leading-none transition-colors duration-300">
                    {item.step}
                  </span>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white text-sm uppercase mb-1 transition-colors duration-300">
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">{item.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 border-l-4 border-slate-900 dark:border-white pl-4 transition-colors duration-300">
              4. DATA SHARING AND DISCLOSURE
            </h3>
            <p className="mb-6 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 p-3 rounded-md text-sm font-medium border-l-4 border-blue-600 dark:border-blue-500 transition-colors duration-300">
              We do not sell your personal data. We only share information with
              trusted third parties to facilitate our services:
            </p>
            <ul className="grid grid-cols-1 gap-4">
              <li className="flex gap-3 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                <span className="text-slate-900 dark:text-white font-bold transition-colors duration-300">●</span>
                <span>
                  <strong className="text-slate-900 dark:text-white transition-colors duration-300">
                    Logistics Partners:
                  </strong>{" "}
                  We share your name, address, and contact number with
                  Shiprocket and associated couriers.
                </span>
              </li>
              <li className="flex gap-3 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                <span className="text-slate-900 dark:text-white font-bold transition-colors duration-300">●</span>
                <span>
                  <strong className="text-slate-900 dark:text-white transition-colors duration-300">Payment Gateways:</strong>{" "}
                  Payments are processed by secure, PCI-compliant third-party
                  processors.
                </span>
              </li>
              <li className="flex gap-3 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                <span className="text-slate-900 dark:text-white font-bold transition-colors duration-300">●</span>
                <span>
                  <strong className="text-slate-900 dark:text-white transition-colors duration-300">
                    Legal Requirements:
                  </strong>{" "}
                  Disclosure if required by law or to protect our legal rights.
                </span>
              </li>
            </ul>
          </section>

          {/* Section 5 & 6 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <section className="p-5 bg-slate-50 dark:bg-gray-700/50 rounded-xl border border-slate-100 dark:border-gray-600 transition-colors duration-300">
              <h3 className="font-bold text-slate-900 dark:text-white mb-2 uppercase text-sm tracking-widest border-b border-slate-200 dark:border-gray-600 pb-2 transition-colors duration-300">
                5. Data Security
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">
                We implement industry-standard measures to prevent data loss.
                Note: no method of transmission over the internet is 100%
                secure.
              </p>
            </section>
            <section className="p-5 bg-slate-50 dark:bg-gray-700/50 rounded-xl border border-slate-100 dark:border-gray-600 transition-colors duration-300">
              <h3 className="font-bold text-slate-900 dark:text-white mb-2 uppercase text-sm tracking-widest border-b border-slate-200 dark:border-gray-600 pb-2 transition-colors duration-300">
                6. Cookies
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">
                We use cookies to remember preferences and cart items. You can
                disable cookies in browser settings, though it may limit
                functionality.
              </p>
            </section>
          </div>

          {/* Section 7 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 border-l-4 border-slate-900 dark:border-white pl-4 transition-colors duration-300">
              7. YOUR LEGAL RIGHTS
            </h3>
            <p className="mb-4 text-sm font-semibold text-slate-500 dark:text-gray-400 uppercase transition-colors duration-300">
              Under Indian law, you have the right to:
            </p>
            <div className="space-y-2">
              {[
                "Request access to personal data.",
                "Request correction of inaccurate data.",
                "Request deletion of data (subject to tax/legal retention).",
              ].map((right, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg text-sm text-slate-700 dark:text-gray-300 transition-colors duration-300"
                >
                  <span className="w-2 h-2 bg-slate-900 dark:bg-white rounded-full transition-colors duration-300"></span>
                  {right}
                </div>
              ))}
            </div>
          </section>

          {/* Footer / Grievance Officer */}
          <footer className="mt-12 p-8 bg-slate-900 rounded-2xl text-white">
            <h3 className="text-xl font-bold mb-6 border-l-4 border-white pl-4 uppercase tracking-widest">
              8. CONTACT & GRIEVANCE
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <p className="font-bold text-white text-lg">
                 Brightex Solutions Pvt Ltd
                </p>
                <p className="text-xs text-slate-400 leading-relaxed uppercase">
                  No 5 and 6, Krishnappa Compound, Jaraganahally, JP Nagar 7th
                  Phase, Bengaluru, Karnataka, 560078
                </p>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-slate-700 pb-2">
                  <span className="font-bold text-slate-400 uppercase text-[10px]">
                    GSTIN
                  </span>
                  <span className="font-mono text-slate-200">
                    29AAICB6992P1ZL
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-700 pb-2">
                  <span className="font-bold text-slate-400 uppercase text-[10px]">
                    Email
                  </span>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-slate-200 text-sm">Jplapzop@gmail.com</span>
                    <span className="text-slate-200 text-sm">btxinwardoutward@gmail.com</span>
                  </div>
                </div>
                <div className="flex justify-between border-b border-slate-700 pb-2">
                  <span className="font-bold text-slate-400 uppercase text-[10px]">
                    Phone
                  </span>
                  <span className="text-slate-200">+91 9353035776</span>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
